import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "h-10 px-2 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";

const STATUS_LABEL = { draft: "Draft", sent: "Sent · awaiting payment", paid: "Paid", void: "Void" };
const STATUS_CLASS = {
  draft: "text-ink/50",
  sent: "text-amber-700",
  paid: "text-tea-text",
  void: "text-ink/30",
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState(null);
  const [clients, setClients] = useState([]);
  const [connectStatus, setConnectStatus] = useState(null);
  const [adding, setAdding] = useState(Boolean(location.state?.prefill));
  const [sendingId, setSendingId] = useState(null);
  const [checkoutUrls, setCheckoutUrls] = useState({});
  const prefill = location.state?.prefill;
  const [form, setForm] = useState({
    client_id: "",
    new_client_name: "",
    new_client_email: "",
    description: prefill?.description || "",
    amount: prefill?.amount ?? "",
    total_cost: prefill?.total_cost ?? null,
    margin_pct: prefill?.margin_pct ?? null,
  });

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/billing/connect/status").then((r) => setConnectStatus(r.data));
  };
  useEffect(load, []);

  // Clear the router state once consumed so a page refresh doesn't re-open the form with stale prefill.
  useEffect(() => { if (prefill) navigate(location.pathname, { replace: true, state: {} }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!form.description || !form.amount) {
      toast.error("Description and amount are required");
      return;
    }
    if (!form.client_id && !form.new_client_name) {
      toast.error("Pick a client or enter a new one");
      return;
    }
    try {
      await api.post("/invoices", {
        client_id: form.client_id || null,
        new_client: form.client_id ? null : { name: form.new_client_name, email: form.new_client_email || null },
        description: form.description,
        amount: parseFloat(form.amount),
        total_cost: form.total_cost,
        margin_pct: form.margin_pct,
      });
      toast.success("Invoice created");
      setAdding(false);
      setForm({ client_id: "", new_client_name: "", new_client_email: "", description: "", amount: "", total_cost: null, margin_pct: null });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const send = async (invoiceId) => {
    setSendingId(invoiceId);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/send`);
      setCheckoutUrls((u) => ({ ...u, [invoiceId]: data.checkout_url }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSendingId(null);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Payment link copied"));
  };

  if (!invoices) return <p className="text-ink/50 py-10">Loading…</p>;

  const connected = Boolean(connectStatus?.charges_enabled);

  return (
    <div className="space-y-6 animate-rise" data-testid="invoices-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Get paid</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">Invoices</h1>
          <p className="text-ink/60 mt-2 text-sm sm:text-base">Bill your client and let them pay by card, Klarna or Clearpay · straight into your own bank account.</p>
        </div>
        <button onClick={() => setAdding((a) => !a)} data-testid="invoice-new-button"
          className="h-11 px-4 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium flex items-center gap-2 transition-colors duration-200">
          <Plus size={16} /> New invoice
        </button>
      </div>

      {!connected && (
        <div className="bg-surface border border-dashed border-sand rounded-lg p-4 text-sm text-ink/60" data-testid="invoice-connect-warning">
          You'll need to <a href="/settings" className="text-tea-text underline">connect Stripe in Settings</a> before you can send an invoice.
        </div>
      )}

      {adding && (
        <section className="bg-surface border border-black/5 rounded-lg p-6 space-y-4" data-testid="invoice-new-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Client</label>
            <select className={`${inputCls} mt-1`} data-testid="invoice-client-select"
              value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">New client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {!form.client_id && (
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Client name" className={inputCls} data-testid="invoice-new-client-name"
                value={form.new_client_name} onChange={(e) => setForm({ ...form, new_client_name: e.target.value })} />
              <input placeholder="Client email (optional)" className={inputCls} data-testid="invoice-new-client-email"
                value={form.new_client_email} onChange={(e) => setForm({ ...form, new_client_email: e.target.value })} />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Description (what your client sees)</label>
            <input className={`${inputCls} mt-1`} data-testid="invoice-description-input"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="max-w-[160px]">
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Amount &pound;</label>
            <input type="number" step="0.01" min="0" className={`${inputCls} mt-1 font-mono-num`} data-testid="invoice-amount-input"
              value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <button onClick={create} data-testid="invoice-create-button"
            className="h-11 px-5 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium transition-colors duration-200">
            Save draft
          </button>
        </section>
      )}

      {invoices.length === 0 ? (
        <div className="bg-surface border border-dashed border-sand rounded-lg p-10 text-center">
          <p className="text-ink/60 text-sm">No invoices yet. Price a job on the <a href="/quote" className="text-tea-text underline">Quote page</a> and turn it into one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="invoice-list">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-surface border border-black/5 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`invoice-row-${inv.id}`}>
              <div>
                <p className="font-medium text-sm">{inv.description}</p>
                <p className="text-xs text-ink/50">{inv.client_name} &middot; <span className={STATUS_CLASS[inv.status]}>{STATUS_LABEL[inv.status]}</span></p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono-num text-sm">{gbp(inv.amount)}</span>
                {inv.status === "draft" && (
                  <button onClick={() => send(inv.id)} disabled={sendingId === inv.id || !connected} data-testid={`invoice-send-${inv.id}`}
                    className="h-9 px-3 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-xs font-medium flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-60">
                    <Send size={13} /> {sendingId === inv.id ? "Sending…" : "Send"}
                  </button>
                )}
                {checkoutUrls[inv.id] && (
                  <button onClick={() => copyLink(checkoutUrls[inv.id])} data-testid={`invoice-copy-${inv.id}`}
                    className="h-9 px-3 rounded-lg border border-sand bg-white hover:bg-sand/40 text-xs font-medium flex items-center gap-1.5 transition-colors duration-200">
                    <Copy size={13} /> Copy link
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!["trialing", "active", "manual", "past_due"].includes(user?.subscription_status) && (
        <p className="text-xs text-ink/40">Free plan: 3 invoices a month. <a href="/pricing" className="text-tea-text underline">Upgrade</a> for unlimited.</p>
      )}
    </div>
  );
}
